import { ChemistryChip } from "@/design-system/Chemistry/Chemistry";
import { ChemistrySelectPanel } from "@/design-system/Chemistry/ChemistrySelectPanel";
import { PlayerCardWithMetaRating } from "@/design-system/PlayerCard/PlayerCard";
import CloseButton from "@/design-system/SpecificButtons/CloseButton/CloseButton";
import { zeroChemMetaRating } from "@repo/shared/hooks";
import { ComparePlayerSlot } from "@repo/shared/redux";
import { Chemistry, DetailedPlayer, Player } from "@repo/shared/types";

import { BestMetaRatingStar } from "./BestMetaRatingStar";
import { CARD_BASE_HEIGHT } from "./compareLayout";

/** ChemistryChip's height-ish offset up from the card art's bottom edge. */
const CHEM_BUTTON_OFFSET = 44;

/**
 * One compared column's card cluster: the meta-rating card with the winner
 * star, the remove button and the chemistry flask + select panel overlaid.
 *
 * `variant` picks the overlay geometry. Desktop columns have slack around the
 * card, so the close/panel buttons overhang the corners (`-top-2 -right-2`);
 * mobile columns sit flush on the scroller's edges, where an overhang past the
 * first/last column would open a phantom x-scroll — everything stays inside
 * (`right-0` / `left-0`), and the flask tracks the scaled card art's bottom.
 */
export const ComparePlayerCard = ({
  player,
  slot,
  variant,
  scale = 1,
  isMetaRatingWinner,
  gradedRoleId,
  onRemove,
  setSlotChemistry,
  setSlotChemStyleId,
}: {
  player: DetailedPlayer;
  /** The column's saved chemistry/chemstyle; undefined while not yet slotted. */
  slot?: ComparePlayerSlot;
  variant: "desktop" | "mobile";
  /** Mobile: the card's scale inside its column. Desktop renders at 1. */
  scale?: number;
  isMetaRatingWinner: boolean;
  /** The selected graded role; the player's own best role when absent. */
  gradedRoleId?: number;
  onRemove: (player: Player) => void;
  /** Slot chemistry/chemstyle writers (see useCompareData). */
  setSlotChemistry: (player: Player, chemistry: Chemistry) => void;
  setSlotChemStyleId: (player: Player, chemStyleId: number | null) => void;
}) => {
  const tierMetaRating = zeroChemMetaRating(player, gradedRoleId);
  const desktop = variant === "desktop";
  return (
    <div className="flex justify-center">
      <div className="relative">
        <PlayerCardWithMetaRating
          player={player}
          scale={scale}
          hideBackground
          hideBorder
          hideLabel
          hideNewTag
          hoverable={false}
          hidePlayerRoleName={false}
          chemStyleId={slot?.chemstyleId ?? undefined}
          tierMetaRating={tierMetaRating}
          playOnShow
        />
        {isMetaRatingWinner && (
          <BestMetaRatingStar
            player={player}
            scale={scale}
            tierMetaRating={tierMetaRating}
          />
        )}
        <CloseButton
          className={
            desktop ? "absolute -top-2 -right-2" : "absolute top-2 right-0"
          }
          onClick={() => onRemove(player)}
        />
        {/* Chemistry display only — the flask is display, the panel is the
            trigger. Anchored to the card art's bottom edge (CARD_BASE_HEIGHT
            × scale), not the wrapper's — the meta chip + role name below
            would drag a bottom-* offset off the card. Full chem: the
            comparison grades everyone at 3. */}
        <ChemistryChip
          chemistry={slot?.chemistry ?? 3}
          className={
            desktop
              ? "pointer-events-none absolute left-3 top-[208px]"
              : "pointer-events-none absolute left-2"
          }
          style={
            desktop
              ? undefined
              : { top: CARD_BASE_HEIGHT * scale - CHEM_BUTTON_OFFSET }
          }
        />
        <ChemistrySelectPanel
          className={
            desktop ? "absolute -top-2 -left-2" : "absolute top-2 left-0"
          }
          presentation={desktop ? undefined : "sheet"}
          player={player}
          chemistry={slot?.chemistry ?? 3}
          chemStyleId={slot?.chemstyleId ?? null}
          metaRatings={player.metaRatings}
          playerRoleId={gradedRoleId ?? player.playerRoleId}
          onChemistryChange={(chemistry) => setSlotChemistry(player, chemistry)}
          onChemStyleChange={(chemStyleId) =>
            setSlotChemStyleId(player, chemStyleId)
          }
        />
      </div>
    </div>
  );
};
